<template>
  <div>
    <PageHeader
      has-return
      :page="pageInfo"
      :actions="pageActions"
      left-side-class="col-12 col-lg-8"
      right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
      @onReturn="goToActionPlanTab"
    />
    <div v-if="!isNewActionPlan" class="mt-2">
      <Board :details="boardInfo" />
    </div>
    <div class="mt-4">
      <ore-action-plan
        ref="oreActionPlanRef"
        :academic-year-id="academicYearId"
        :is-read-only="isReadOnly"
        :is-new-action-plan="isNewActionPlan"
        :initial-data="actionPlanData"
      />
    </div>
    <div class="mt-4">
      <search-student
        ref="searchStudentRef"
        :is-read-only="isReadOnly"
        :academic-year-id="actionPlanData.academicYearId || academicYearId"
        :action-plan-id="actionPlanData.actionPlanId"
        :student-data="actionPlanData.students"
      />
    </div>
    <div class="mt-4">
      <alternative-information
        ref="alternativeInformationRef"
        :is-read-only="isReadOnly"
        :is-new-action-plan="isNewActionPlan"
        :action-plan-id="actionPlanId"
        :academic-year-id="academicYearId"
        :action-plan-data="actionPlanData"
        @refresh="getActionPlanById"
      />
    </div>
  </div>
</template>
<script>
// libs
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import Board from 'Board';
import SectionTitle from 'SectionTitle';

// components
import {
  OreActionPlan,
  SearchStudent,
  AlternativeInformation,
} from './components';

// api
import * as api from '@/views/pei/location-alternatives/_api';

// mixins
import alert from '@/mixin/alert.mixin';

// constants
import alternativeLocationActionPlanStatus from '@/utils/constants/tapRequest/alternativeLocationActionPlanStatus.js';
import alternativeLocationActionPlanTypes from '@/utils/constants/tapRequest/alternativeLocationActionPlanTypes.js';

const TABS = {
  SCHOOL: 0,
  ACTIONPLAN: 1,
};

export default {
  name: 'CreateActionPlan',
  mixins: [alert],
  components: {
    ActionButton,
    Board,
    SectionTitle,
    PageHeader,
    OreActionPlan,
    SearchStudent,
    AlternativeInformation,
  },
  data: () => ({
    actionPlanData: {},
  }),
  computed: {
    academicYearId() {
      return +this.$route.params.academicYearId;
    },
    actionPlanId() {
      return this.$route.params.actionPlanId
        ? +this.$route.params.actionPlanId
        : this.actionPlanData.actionPlanId;
    },
    schoolAlternativeLocationId() {
      return this.$route.params.schoolAlternativeLocationId
        ? +this.$route.params.schoolAlternativeLocationId
        : null;
    },
    locationSchoolId() {
      return this.actionPlanData.locationSchoolId;
    },
    alternativeLocationId() {
      return this.actionPlanData.name?.id;
    },
    isNewActionPlan() {
      return !this.actionPlanId;
    },
    isSubmitted() {
      return [
        alternativeLocationActionPlanStatus.Extendido,
        alternativeLocationActionPlanStatus.Sometido,
        alternativeLocationActionPlanStatus.EnProceso,
        alternativeLocationActionPlanStatus.NoAmeritaAccion,
        alternativeLocationActionPlanStatus.Atendido,
      ].includes(this.actionPlanData.status?.id);
    },
    isReadOnly() {
      return this.isSubmitted;
    },
    pageInfo() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Crear Plan de Acción',
        routes: ['Inicio', 'PEI', 'Crear Plan de Acción'],
      };
    },
    pageActions() {
      return [
        {
          name: 'Guardar',
          icon: 'fa fa-save',
          variant: 'outline-success',
          text: 'Guardar',
          visible: !this.isSubmitted,
          click: this.handleSave,
        },
        {
          name: 'Someter',
          icon: 'fa fa-flag',
          variant: 'success',
          text: 'Someter',
          visible: !this.isSubmitted,
          click: () => this.handleSave(true),
        },
      ];
    },
    boardInfo() {
      const { category, name, salonSpecialty } = this.actionPlanData;
      return [
        [
          {
            label: 'Clasificación de Alternativa:',
            data: category?.name || '-',
            icon: 'fas fa-ballot-check',
          },
          {
            label: 'Nombre de Alternativa:',
            data: name?.name || '-',
            icon: 'fas fa-file-invoice',
          },
          {
            label: 'Especialidad',
            data: salonSpecialty?.name || '-',
            icon: 'fas fa-ballot-check',
          },
        ],
      ];
    },
  },
  async mounted() {
    if (this.actionPlanId) this.getActionPlanById();
    else this.getAlternativeLocationActionPlanInfo();
  },
  methods: {
    goToActionPlanTab() {
      this.$router.push({
        name: 'location-alternatives',
        query: {
          tab: TABS.ACTIONPLAN,
        },
      });
    },
    async getActionPlanById(id = this.actionPlanId) {
      const { data } = await api
        .getAlternativeLocationActionPlanById(id)
        .catch((error) => {
          this.ShowToast('Error', error.message, 'error');
        });
      if (data) this.actionPlanData = data;
    },
    async getAlternativeLocationActionPlanInfo() {
      if (this.schoolAlternativeLocationId && this.academicYearId) {
        const response = await api
          .getAlternativeLocationActionPlan(
            this.schoolAlternativeLocationId,
            this.academicYearId
          )
          .catch((error) => {
            this.ShowToast('Error', error.message, 'error');
          });
        if (response?.data) {
          this.actionPlanData = response.data;
        }
      }
    },
    async validateForm() {
      const oreActionPlanRef = this.$refs.oreActionPlanRef;
      if (!oreActionPlanRef) return false;

      const isValidOreActionPlan = await oreActionPlanRef.validateForm();

      if (this.isReadOnly) return isValidOreActionPlan;

      const isValidAlternativeInformation = await this.$refs.alternativeInformationRef.validateForm();

      return isValidOreActionPlan && isValidAlternativeInformation;
    },
    async handleSave(submitted = false) {
      const isValid = await this.validateForm();
      if (!isValid) {
        this.ShowToast(
          'Error',
          'Favor de completar los campos requeridos.',
          'error'
        );
        return;
      }

      if (submitted) {
        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro que desea someter el Plan de Acción?',
          'Si',
          'No'
        );

        if (!confirmed) return;
      }

      const formData = this.$refs.oreActionPlanRef?.getModel() ?? {};
      const alternativeInformationData =
        this.$refs.alternativeInformationRef?.getModel() ?? {};

      const searchStudentData = this.$refs.searchStudentRef?.getModel();
      if (formData) {
        const payload = {
          submitted,
          actionPlanId: this.actionPlanId,
          academicYearId: this.academicYearId,
          locationSchoolId: formData.school?.id ?? this.locationSchoolId,
          schoolAlternativeLocationId: this.schoolAlternativeLocationId,
          alternativeLocationId:
            alternativeInformationData.alternativeName?.id ??
            this.alternativeLocationId,
          statusId: alternativeLocationActionPlanStatus.Borrador,
          actionPlanTypeId:
            this.isNewActionPlan && !this.actionPlanId
              ? alternativeLocationActionPlanTypes.Creacion
              : alternativeLocationActionPlanTypes.Actualizacion,
          venueAvailabilityStatusConfirmed:
            formData.venueAvailabilityStatusConfirmed,
          staffAssignmentResourceConfirmed:
            formData.staffAssignmentResourceConfirmed,
          equipmentMaterialsResourcesReady:
            formData.equipmentMaterialsResourcesReady,
          isForPreschoolStudents:
            formData.isForPreschoolStudents,
          students: searchStudentData,
          studentCount: alternativeInformationData?.studentCount,
        };

        const {
          data: actionPlanId,
        } = await api
          .saveAlternativeLocationActionPlan(payload)
          .catch((error) => {
            this.ShowToast('Error', error.message, 'error');
          });
        if (actionPlanId) {
          this.ShowToast(
            '',
            'El plan de acción se ha guardado correctamente.',
            'success'
          );
          if (this.isNewActionPlan && !this.actionPlanId) {
            this.$router.replace({
              name: this.$route.name,
              params: {
                ...this.$route.params,
                schoolAlternativeLocationId:
                  this.schoolAlternativeLocationId || 'null',
                actionPlanId,
              },
            });
          }
          this.actionPlanData = { actionPlanId };
          await this.getActionPlanById(actionPlanId);
        }
      }
    },
  },
};
</script>
